import React, { useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import * as QRCode from 'qrcode';
import { obtenerQrFirmasService, QrFirma } from '../../../service/tickets/Tickets';

type Props = {
  tickets: any[];
  handleClose: () => void;
  /** 'ticket' = folio simple (default) | 'gafete' = credencial con nombre y empresa */
  plantilla?: 'ticket' | 'gafete';
};

// Gafete: 90x130mm, 3 columnas x 3 filas por página tabloide
const GAFETE_W = 90;
const GAFETE_H = 130;
const GAFETES_PER_ROW = 3;
const GAFETES_PER_PAGE = 9;
const GAFETE_MARGIN_X = (279 - GAFETES_PER_ROW * GAFETE_W) / 2;
const GAFETE_MARGIN_Y = (432 - 3 * GAFETE_H) / 2;

const TICKET_W         = 80;   // mm (8cm)
const TICKET_H         = 50;   // mm (5cm)
const PAGE_MARGIN_X    = 19.5; // mm — centrado horizontal en tabloide (279 - 240) / 2
const PAGE_MARGIN_Y    = 16;   // mm — centrado vertical en tabloide (432 - 400) / 2
const GAP              = 0;    // mm — tickets pegados, separados solo por línea de corte
const TICKETS_PER_ROW  = 3;
const TICKETS_PER_PAGE = 24;   // 3 columnas × 8 filas

const ROWS = TICKETS_PER_PAGE / TICKETS_PER_ROW; // 8


const loadImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const drawCutLines = (doc: jsPDF) => {
  doc.setDrawColor(130, 130, 130);
  doc.setLineWidth(0.4);

  const blockW = TICKETS_PER_ROW * TICKET_W;
  const blockH = ROWS * TICKET_H;
  const x0 = PAGE_MARGIN_X;
  const y0 = PAGE_MARGIN_Y;
  const x1 = x0 + blockW;
  const y1 = y0 + blockH;

  // Líneas verticales: borde izq + entre columnas + borde der
  for (let j = 0; j <= TICKETS_PER_ROW; j++) {
    const xLine = x0 + j * TICKET_W;
    doc.line(xLine, y0, xLine, y1);
  }

  // Líneas horizontales: borde top + entre filas + borde bottom
  for (let i = 0; i <= ROWS; i++) {
    const yLine = y0 + i * TICKET_H;
    doc.line(x0, yLine, x1, yLine);
  }
};

const drawGafeteCutLines = (doc: jsPDF) => {
  doc.setDrawColor(130, 130, 130);
  doc.setLineWidth(0.4);
  const x0 = GAFETE_MARGIN_X;
  const y0 = GAFETE_MARGIN_Y;
  for (let j = 0; j <= GAFETES_PER_ROW; j++) {
    const xLine = x0 + j * GAFETE_W;
    doc.line(xLine, y0, xLine, y0 + 3 * GAFETE_H);
  }
  for (let i = 0; i <= 3; i++) {
    const yLine = y0 + i * GAFETE_H;
    doc.line(x0, yLine, x0 + GAFETES_PER_ROW * GAFETE_W, yLine);
  }
};

const TicketsPdf: React.FC<Props> = ({ tickets, handleClose, plantilla = 'ticket' }) => {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfError, setPdfError]     = useState<boolean>(false);
  const blobUrlRef                  = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const generatePDF = async (): Promise<string | null> => {
    if (!tickets.length) return null;

    // El backend firma cada payload (HMAC) y deriva el evento real del ticket;
    // el QR se genera exactamente con lo que regresa el servidor.
    const firmas = await obtenerQrFirmasService(tickets.map((t) => t.UUID));
    const firmasPorUUID = new Map<string, QrFirma>(firmas.map((f) => [f.ticketcash, f]));

    const doc = new jsPDF({ format: 'tabloid', orientation: 'portrait' });
    const nuevoDisenoBase64 = await loadImageAsBase64(process.env.PUBLIC_URL + '/media/logos/El_Banco_de_Hoy.png');

    if (plantilla === 'gafete') {
      for (let i = 0; i < tickets.length; i++) {
        const ticket = tickets[i];
        const posInPage = i % GAFETES_PER_PAGE;
        const row = Math.floor(posInPage / GAFETES_PER_ROW);
        const col = posInPage % GAFETES_PER_ROW;

        if (posInPage === 0 && i > 0) {
          drawGafeteCutLines(doc);
          doc.addPage();
        }

        const x = GAFETE_MARGIN_X + col * GAFETE_W;
        const y = GAFETE_MARGIN_Y + row * GAFETE_H;

        const firmaQr = firmasPorUUID.get(ticket.UUID);
        if (!firmaQr) {
          throw new Error(`No se obtuvo la firma del QR para el ticket ${ticket.folio}`);
        }
        const qrData = JSON.stringify({
          ticketcash: firmaQr.ticketcash,
          folio: firmaQr.folio,
          version: firmaQr.version,
          evento: firmaQr.evento,
          firma: firmaQr.firma,
        });
        const qrDataUrl = await QRCode.toDataURL(qrData, {
          margin: 1,
          width: 300,
          color: { dark: '#000000', light: '#ffffff' },
        });

        // Encabezado con el logo del evento (mismo arte de los tickets)
        if (nuevoDisenoBase64) {
          doc.addImage(nuevoDisenoBase64, 'PNG', x + 5, y + 5, GAFETE_W - 10, 30);
        }

        // QR centrado
        const qrSize = 55;
        doc.addImage(qrDataUrl, 'PNG', x + (GAFETE_W - qrSize) / 2, y + 42, qrSize, qrSize);

        // Nombre del asistente / empresa / evento / folio
        doc.setTextColor(30, 30, 30);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text(
          ticket.cliente?.nombre ?? 'ASISTENTE',
          x + GAFETE_W / 2,
          y + 106,
          { align: 'center', maxWidth: GAFETE_W - 10 }
        );
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.text(
          ticket.cliente?.grupo?.nombre ?? '',
          x + GAFETE_W / 2,
          y + 113,
          { align: 'center', maxWidth: GAFETE_W - 10 }
        );
        doc.setFontSize(9);
        doc.setTextColor(110, 110, 110);
        doc.text(
          `${ticket.evento?.nombre ?? ''}  ·  Folio: ${ticket.folio ?? ''}`,
          x + GAFETE_W / 2,
          y + 122,
          { align: 'center', maxWidth: GAFETE_W - 10 }
        );
      }

      drawGafeteCutLines(doc);

      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = url;
      return url;
    }

    for (let i = 0; i < tickets.length; i++) {
      const ticket    = tickets[i];
      const posInPage = i % TICKETS_PER_PAGE;
      const row       = Math.floor(posInPage / TICKETS_PER_ROW);
      const col       = posInPage % TICKETS_PER_ROW;

      if (posInPage === 0 && i > 0) {
        drawCutLines(doc);
        doc.addPage();
      }

      const x = PAGE_MARGIN_X + col * (TICKET_W + GAP);
      const y = PAGE_MARGIN_Y + row * (TICKET_H + GAP);

      // Imagen de fondo completa del ticket
      if (nuevoDisenoBase64) {
        doc.addImage(nuevoDisenoBase64, 'PNG', x, y, TICKET_W, TICKET_H);
      }

      // --- QR en el recuadro blanco (derecha) ---
      const firmaQr = firmasPorUUID.get(ticket.UUID);
      if (!firmaQr) {
        throw new Error(`No se obtuvo la firma del QR para el ticket ${ticket.folio}`);
      }
      const qrData = JSON.stringify({
        ticketcash: firmaQr.ticketcash,
        folio: firmaQr.folio,
        version: firmaQr.version,
        evento: firmaQr.evento,
        firma: firmaQr.firma,
      });
      const qrDataUrl = await QRCode.toDataURL(qrData, {
        margin: 1,
        width: 150,
        color: { dark: '#000000', light: '#ffffff' },
      });

      const qrSize = 27;
      const qrX    = x + 49;
      const qrY    = y + 10.2;
      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

      // --- Folio sin fondo de color ---
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(
        `Folio:${ticket.folio ?? ''}`,
        x + 62.5,
        y + 43,
        { align: 'center' }
      );
    }

    // Dibujar líneas de corte encima de los tickets de la última página
    drawCutLines(doc);

    const blob = doc.output('blob');
    const url  = URL.createObjectURL(blob);
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    blobUrlRef.current = url;
    return url;
  };

  useEffect(() => {
    if (tickets.length === 0) return;
    setPdfError(false);
    generatePDF()
      .then((url) => {
        if (url) setPdfBlobUrl(url);
        else setPdfError(true);
      })
      .catch((err) => {
        console.error('Error generando PDF de tickets:', err);
        setPdfError(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, plantilla]);

  return (
    <div className="w-100">
      {pdfError ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
          <span className="text-danger text-center">No se pudo generar el PDF. Intenta de nuevo.</span>
        </div>
      ) : pdfBlobUrl ? (
        <iframe
          src={pdfBlobUrl}
          title="Vista previa — Tickets con QR"
          width="100%"
          height="620px"
          style={{ border: 'none', display: 'block' }}
        />
      ) : (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary mb-3" role="status" />
            <div className="text-muted fs-6">Generando PDF con tickets...</div>
          </div>
        </div>
      )}
      <div className="d-flex justify-content-end p-3">
        <button onClick={handleClose} className="btn btn-secondary">
          Cerrar
        </button>
      </div>
    </div>
  );
};

export { TicketsPdf };
